import React, { useState } from 'react'
import initSqlJs from 'sql.js'

const DatabaseStructureTest: React.FC = () => {
  const [dbStructure, setDbStructure] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const analyzeDatabaseStructure = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('🔍 Analyzing database structure...')
      
      // Initialize SQL.js
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/${file}`
      })

      const databases = [
        '/bank/L11/C1/B01/TN/questions.db',
        '/bank/L11/C1/B01/DS/questions.db',
        '/bank/L11/C1/B01/TL/questions.db'
      ]

      const allStructures = []

      for (const dbPath of databases) {
        try {
          console.log(`📖 Loading database: ${dbPath}`)
          
          const response = await fetch(dbPath)
          if (!response.ok) {
            console.log(`❌ Database not found: ${dbPath}`)
            continue
          }

          const arrayBuffer = await response.arrayBuffer()
          const db = new SQL.Database(new Uint8Array(arrayBuffer))
          
          // Get tables
          const tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
          if (tables.length === 0) {
            console.log(`⚠️ No tables found in ${dbPath}`)
            continue
          }

          const tableNames = tables[0].values.map(row => row[0] as string)
          console.log(`📋 Tables found: ${tableNames.join(', ')}`)

          for (const tableName of tableNames) {
            try {
              // Get table schema
              const schema = db.exec(`PRAGMA table_info(${tableName})`)
              const columns = schema[0]?.values.map(row => ({
                name: row[1] as string,
                type: row[2] as string,
                notNull: row[3] as number,
                defaultValue: row[4],
                primaryKey: row[5] as number
              })) || []

              // Get sample data
              const sampleData = db.exec(`SELECT * FROM ${tableName} LIMIT 3`)
              const sampleRows = sampleData[0]?.values || []
              const sampleColumns = sampleData[0]?.columns || []

              // Create sample objects
              const sampleObjects = sampleRows.map(row => {
                const obj: any = {}
                sampleColumns.forEach((col, index) => {
                  obj[col] = row[index]
                })
                return obj
              })

              allStructures.push({
                database: dbPath,
                table: tableName,
                columns,
                sampleCount: sampleRows.length,
                sampleData: sampleObjects
              })

              console.log(`✅ Analyzed table ${tableName} in ${dbPath}`)

            } catch (e) {
              console.error(`❌ Error analyzing table ${tableName}:`, e)
            }
          }

          db.close()

        } catch (error) {
          console.error(`❌ Error loading database ${dbPath}:`, error)
        }
      }

      setDbStructure(allStructures)
      console.log('📊 Database analysis complete:', allStructures)

    } catch (err) {
      console.error('❌ Error in database analysis:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Database Structure Analysis</h1>
      
      <div className="mb-6">
        <button
          onClick={analyzeDatabaseStructure}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Analyzing...' : 'Analyze Database Structure'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}

      {dbStructure.length > 0 && (
        <div className="space-y-8">
          {dbStructure.map((structure, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-6 bg-white">
              <h2 className="text-xl font-semibold mb-4">
                {structure.database} - {structure.table}
              </h2>
              
              {/* Column Schema */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">📋 Columns ({structure.columns.length})</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 border-b text-left font-medium">Column Name</th>
                        <th className="px-4 py-2 border-b text-left font-medium">Type</th>
                        <th className="px-4 py-2 border-b text-left font-medium">Not Null</th>
                        <th className="px-4 py-2 border-b text-left font-medium">Default</th>
                        <th className="px-4 py-2 border-b text-left font-medium">Primary Key</th>
                      </tr>
                    </thead>
                    <tbody>
                      {structure.columns.map((column: any, colIndex: number) => (
                        <tr key={colIndex} className={colIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className={`px-4 py-2 border-b font-mono text-sm ${
                            column.name.toLowerCase().includes('answer') || 
                            column.name.toLowerCase().includes('dap_an') ||
                            column.name.toLowerCase().includes('correct') ||
                            column.name.toLowerCase().includes('dung')
                              ? 'bg-yellow-100 font-bold' : ''
                          }`}>
                            {column.name}
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{column.type}</td>
                          <td className="px-4 py-2 border-b text-sm">{column.notNull ? 'Yes' : 'No'}</td>
                          <td className="px-4 py-2 border-b text-sm">{column.defaultValue || 'NULL'}</td>
                          <td className="px-4 py-2 border-b text-sm">{column.primaryKey ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sample Data */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">📊 Sample Data ({structure.sampleCount} rows)</h3>
                {structure.sampleData.map((sample: any, sampleIndex: number) => (
                  <div key={sampleIndex} className="mb-4 p-4 bg-gray-50 rounded border">
                    <h4 className="font-medium mb-2">Row {sampleIndex + 1}:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(sample).map(([key, value]) => (
                        <div key={key} className={`p-2 bg-white rounded border text-sm ${
                          key.toLowerCase().includes('answer') || 
                          key.toLowerCase().includes('dap_an') ||
                          key.toLowerCase().includes('correct') ||
                          key.toLowerCase().includes('dung')
                            ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                        }`}>
                          <div className="font-medium text-gray-700">{key}:</div>
                          <div className="font-mono text-xs break-all">
                            {value === null ? 'NULL' : 
                             value === undefined ? 'UNDEFINED' :
                             typeof value === 'string' && value.length > 100 ? 
                               value.substring(0, 100) + '...' : 
                               String(value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Answer Fields Analysis */}
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                <h3 className="text-lg font-medium mb-2">🎯 Answer Fields Found:</h3>
                <div className="space-y-2">
                  {structure.columns
                    .filter((col: any) => 
                      col.name.toLowerCase().includes('answer') || 
                      col.name.toLowerCase().includes('dap_an') ||
                      col.name.toLowerCase().includes('correct') ||
                      col.name.toLowerCase().includes('dung')
                    )
                    .map((col: any, index: number) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="font-mono bg-white px-2 py-1 rounded border text-sm">
                          {col.name}
                        </span>
                        <span className="text-sm text-gray-600">({col.type})</span>
                        {structure.sampleData.length > 0 && (
                          <span className="text-sm text-blue-600">
                            Sample: {String(structure.sampleData[0][col.name] || 'NULL')}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold mb-2">📋 Analysis Purpose:</h3>
        <ul className="text-sm space-y-1">
          <li>• Identify all column names in the database</li>
          <li>• Find fields containing correct answers (highlighted in yellow)</li>
          <li>• Examine sample data to understand data format</li>
          <li>• Help fix the scoring logic by using correct field names</li>
        </ul>
      </div>
    </div>
  )
}

export default DatabaseStructureTest